import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Section, Cell, Caption } from '@telegram-apps/telegram-ui'
import { Link } from 'react-router-dom'
import { Briefcase, Home, Check } from 'lucide-react'

import { api } from '../../lib/api'
import type { TaskCalendarEvent } from '../../lib/types'
import { formatDateLong, formatDeadline, todayISO } from '../../lib/format'
import PageHeader from '../../components/PageHeader'
import MonthGrid from '../../components/MonthGrid'
import { SkeletonList } from '../../components/Skeleton'

type View = 'day' | 'week' | 'month'

export default function MyCalendar() {
  const [view, setView] = useState<View>('week')
  const [selectedDate, setSelectedDate] = useState(todayISO())

  // В режиме месяца тянем окно пошире, чтобы листать на пару месяцев вперёд/назад
  const days = view === 'day' ? 1 : view === 'week' ? 7 : 120

  const { data: tasks, isLoading } = useQuery<TaskCalendarEvent[]>({
    queryKey: ['my-calendar', days],
    queryFn: () => api.get<TaskCalendarEvent[]>(`/my-calendar?days=${days}`),
  })

  const all = tasks ?? []
  const daysWithEvents = new Set(all.map((t) => t.date))
  const filtered =
    view === 'day'   ? all.filter((t) => t.date === selectedDate)
    : view === 'month' ? all.filter((t) => t.date === selectedDate)
    : all

  return (
    <>
      <PageHeader title="Календарь" caption={formatDateLong(new Date())} />

      <div className="seg-row" style={{ ['--seg-cols' as any]: 3 }}>
        <button className={`seg-btn${view === 'day'   ? ' is-active' : ''}`} onClick={() => setView('day')}>День</button>
        <button className={`seg-btn${view === 'week'  ? ' is-active' : ''}`} onClick={() => setView('week')}>Неделя</button>
        <button className={`seg-btn${view === 'month' ? ' is-active' : ''}`} onClick={() => setView('month')}>Месяц</button>
      </div>

      {view === 'month' && (
        <MonthGrid selected={selectedDate} onSelect={setSelectedDate} daysWithEvents={daysWithEvents} />
      )}

      {isLoading ? <SkeletonList count={5} /> : null}

      {view === 'day' && tasks && (
        <Section header={formatDateLong(new Date(selectedDate))}>
          {filtered.length === 0
            ? <Cell><Caption style={{ opacity: 0.6 }}>В этот день пусто</Caption></Cell>
            : filtered.map((t) => <TaskRow key={t.id} t={t} />)
          }
        </Section>
      )}

      {view === 'week' && tasks && <TasksByDay tasks={filtered} />}

      {view === 'month' && tasks && (
        <Section header={formatDateLong(new Date(selectedDate))}>
          {filtered.length === 0
            ? <Cell><Caption style={{ opacity: 0.6 }}>В этот день пусто</Caption></Cell>
            : filtered.map((t) => <TaskRow key={t.id} t={t} />)
          }
        </Section>
      )}
    </>
  )
}

function TasksByDay({ tasks }: { tasks: TaskCalendarEvent[] }) {
  if (tasks.length === 0) {
    return <Cell><Caption style={{ opacity: 0.6 }}>Задач на неделю нет</Caption></Cell>
  }

  const byDay: Record<string, TaskCalendarEvent[]> = {}
  for (const t of tasks) {
    byDay[t.date] = byDay[t.date] || []
    byDay[t.date].push(t)
  }
  const today = todayISO()

  return (
    <>
      {Object.entries(byDay).map(([day, items]) => {
        const isPast = day < today
        const isToday = day === today
        return (
          <Section
            key={day}
            header={
              <span>
                {formatDateLong(new Date(day))}
                {isToday && ' — сегодня'}
                {isPast && !isToday && ' — просрочено'}
              </span>
            }
          >
            {items.map((t) => <TaskRow key={t.id} t={t} />)}
          </Section>
        )
      })}
    </>
  )
}

function TaskRow({ t }: { t: TaskCalendarEvent }) {
  return (
    <Cell
      before={t.category === 'work' ? <Briefcase size={18} /> : <Home size={18} />}
      subtitle={
        <Caption style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          {t.status === 'done'
            ? <><Check size={12} color="var(--success)" /> выполнено</>
            : formatDeadline(t.date)}
        </Caption>
      }
      multiline
      Component={Link}
      {...({ to: `/tasks/${t.id}` } as any)}
    >
      {t.title}
    </Cell>
  )
}
